// DatabaseService.js - Handles communication with the local backend server for export schedules

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If explicitly set in environment, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For development (localhost), use full URL with port
  if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
    return 'http://localhost:5000/api';
  }
  
  // For production, use relative path
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Debug logging to help with environment issues
console.log('🔧 Export DatabaseService Configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  resolved_API_BASE_URL: API_BASE_URL
});

// Save vessel schedules for a specific service to the database via the server
const saveServiceSchedules = async (serviceCode, scheduleData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${serviceCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(scheduleData),
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    console.log(`✅ ${serviceCode} schedule data saved via server.`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving ${serviceCode} schedule data via server:`, error);
    return false;
  }
};

// Get the latest schedule data for a specific service from the server
const getServiceSchedules = async (serviceCode) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${serviceCode}`);
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`❌ Error retrieving ${serviceCode} schedule data from server:`, error);
    return null;
  }
};

// Get all schedules (for admin/debugging)
const getAllSchedules = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules`);
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error retrieving all schedule data from server:', error);
    return null;
  }
};

// Clear schedule data for a specific service via the server
const clearServiceSchedules = async (serviceCode) => {
  try {
    const response = await fetch(`${API_BASE_URL}/schedules/${serviceCode}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }

    console.log(`✅ Cleared ${serviceCode} schedule data via server.`);
    return true;
  } catch (error) {
    console.error(`❌ Error clearing ${serviceCode} schedule data via server:`, error);
    return false;
  }
};

// Health check to see if server is running
const healthCheck = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Server health check failed:', error);
    return null;
  }
};

export const DatabaseService = {
  saveServiceSchedules,
  getServiceSchedules,
  getAllSchedules,
  clearServiceSchedules,
  healthCheck
};
